import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {ChatContainer, MainContainer, Message, MessageInput, MessageList, TypingIndicator} from "@chatscope/chat-ui-kit-react";
import React, {useCallback, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import WordModalWindow from './WordModalWindow.js'
import AnalizeModalWindow from './AnalizeModalWindow.js'
import {api} from './Api.js'
import WritingRoles from "./WritingRoles.js";
import PreflightLoader, {parseISODuration, usePreflightTimer} from "./PreflightLoader";
import {config} from "./Constants";
import styles from './css/Practice.module.css';

const Translating = ({showNotification}) => {
    const [showWordModal, setShowWordModal] = useState(false);
    const [wordModalData, setWordModalData] = useState(null);
    const [showAnalizeModal, setShowAnalizeModal] = useState(false);
    const [analizeModalData, setAnalizeModalData] = useState(null);
    const [role, setRole] = useState(null);
    const [messages, setMessages] = useState(null);
    const [userMessages, setUserMessages] = useState(null);
    const [waiting, setWaiting] = useState(false);
    const [waiting2, setWaiting2] = useState(false);
    const [typing, setTyping] = useState(false);
    const [inputText, setInputText] = useState("");
    const [currentIndex, setCurrentIndex] = useState(0);
    const [expectedWaitTime, setExpectedWaitTime] = useState(0);
    const timeLeft = usePreflightTimer(expectedWaitTime, typing);

    const navigate = useNavigate();

    const calcUserIndex = useCallback((messages) => {
        if (!messages || messages.length === 0) {
            return 0;
        }

        const userIndexes = messages.map(m => m.userIndex);
        const maxIndex = Math.max(...userIndexes);

        return maxIndex + 1;
    }, []);

    const calcIndex = useCallback((messages) => {
        if (!messages || messages.length === 0) {
            return 0;
        }

        const indexes = messages.filter(m => m.result).map(m => m.index);

        if (indexes.length === 0) {
            return 0;
        }

        const maxIndex = Math.max(...indexes);

        return maxIndex + 1;
    }, []);

    const handleSaveMessage = useCallback(async (innerMessages, result) => {
        let group = userMessages;
        group.messages = innerMessages;
        const response = await api.saveMessages(group, navigate, showNotification);
        if (api.isValidResponse(response)) {
            setUserMessages(response.data);
            if (result) {
                setCurrentIndex(currentIndex + 1);
            }
            setInputText(""); // Clear input on success
        }
        setTyping(false);
    }, [currentIndex, navigate, showNotification, userMessages]);

    const saveMessage = useCallback((text, result, comment, rating) => {
        let innerMessages = [];
        if (userMessages && userMessages.messages !== null) {
            innerMessages = userMessages.messages;
        }
        let message = messages.messages[currentIndex];

        innerMessages = [...innerMessages, {
            messageId: message.messageId,
            userIndex: calcUserIndex(userMessages?.messages),
            type: message.type,
            text: text,
            textRu: message.textRu,
            result: result,
            comment: comment,
            rating: rating || 5
        }];

        setTyping(true);
        handleSaveMessage(innerMessages, result);
    }, [currentIndex, handleSaveMessage, messages, userMessages, calcUserIndex]);

    const handleSmartCheck = useCallback(async (text) => {
        if (config.enablePreflight) {
            const waitRes = await api.fetchWaitTime('/writing/translating/smart', navigate, showNotification);
            if (api.isValidResponse(waitRes)) {
                setExpectedWaitTime(parseISODuration(waitRes.data.expectedWaitTime));
            }
        }
        const response = await api.writingSmartCheck(messages.messages[currentIndex].textRu, text, navigate, showNotification);
        if (api.isValidResponse(response)) {
            saveMessage(text, response.data.rating > 3, response.data.comment, response.data.rating)
            setExpectedWaitTime(0);
        } else {
            setTyping(false);
            setExpectedWaitTime(0);
            // We don't clear input here so user can fix it
        }
    }, [currentIndex, messages, navigate, saveMessage, showNotification]);

    const sendMessage = useCallback(() => {
        const text = inputText;
        if (!text || text.trim().length === 0) {
            return;
        }

        let currentRoleMessage = messages.messages[currentIndex];

        setTyping(true);

        if (currentRoleMessage.text.replace(/\W/g, '').toLowerCase() === text.replace(/\W/g, '').toLowerCase()) {
            saveMessage(text, true, null, 5);
        } else {
            handleSmartCheck(text);
        }
    }, [currentIndex, messages, saveMessage, handleSmartCheck, inputText]);

    const callGetMessages = useCallback(async (roleId) => {
        if (config.enablePreflight) {
            const waitRes = await api.fetchWaitTime('/writing/translating/messages', navigate, showNotification, {roleId: roleId});
            if (api.isValidResponse(waitRes)) {
                setExpectedWaitTime(parseISODuration(waitRes.data.expectedWaitTime));
            }
        }
        const response = await api.getAllMessages(roleId, navigate, showNotification);
        if (api.isValidResponse(response)) {
            setMessages(response.data);
            setWaiting(false);
            setExpectedWaitTime(0);
        } else {
            setWaiting(false);
            setExpectedWaitTime(0);
        }
    }, [navigate, showNotification]);

    const callGetUserMessages = useCallback(async (roleId) => {
        const response = await api.getUserMessages(roleId, true, navigate, showNotification);
        if (api.isValidResponse(response)) {
            setUserMessages(response.data);
            setCurrentIndex(calcIndex(response.data.messages));
            setWaiting2(false);
        }
    }, [navigate, showNotification, calcIndex]);

    const renderInteractiveText = useCallback((text) => {
        const stopWords = new Set([
            "the", "and", "but", "for", "are", "was", "were", "been", "being", "this", "that", "these", "those"
        ]);

        return text
            .split(/(\s+|[.,!?;:]|\b___+\b)/)
            .map((segment, index) => {
                const isWord = /^[a-zA-Zа-яА-ЯёЁčćžšđČĆŽŠĐ]{3,}$/.test(segment);
                const isStopWord = stopWords.has(segment.toLowerCase());

                if (isWord && !isStopWord) {
                    return (
                        <button
                            key={index}
                            className={styles.interactiveWord}
                            onClick={() => {
                                setWordModalData({text, wordEn: segment});
                                setShowWordModal(true);
                            }}
                            type="button"
                        >
                            {segment}
                        </button>
                    );
                }

                return <span key={index}>{segment}</span>;
            });
    }, []);

    const handlePaste = useCallback((evt) => {
        evt.preventDefault();
        const text = evt.clipboardData.getData("text/plain");
        document.execCommand("insertHTML", false, text);
    }, []);

    const setRoleCallback = useCallback((r) => {
        setWaiting(true);
        setWaiting2(true);
        setRole(r);
        callGetMessages(r.roleId);
        callGetUserMessages(r.roleId);
    }, [callGetMessages, callGetUserMessages]);

    const returnToRoleSelection = useCallback(() => {
        if (typing) {
            return;
        }

        setRole(null);
        setMessages(null);
        setUserMessages(null);
        setCurrentIndex(0);
        setInputText("");
        setExpectedWaitTime(0);
        setShowAnalizeModal(false);
        setShowWordModal(false);
    }, [typing]);

    const onCloseCallback = useCallback(() => {
        setShowAnalizeModal(false);
    }, []);

    if (waiting || waiting2) {
        return (
            <div className="common-preloader-form">
                <PreflightLoader expectedWaitTime={expectedWaitTime}/>
            </div>
        )
    }

    if (role === null) {
        return (
            <WritingRoles showNotification={showNotification} type={"TRANSLATING"} setRole={setRoleCallback}/>
        )
    }

    return (
        <main className={styles.page}>
            {showWordModal && (
                <div className={styles.modalOverlay}>
                    <WordModalWindow
                        wordEn={wordModalData.wordEn}
                        text={wordModalData.text}
                        onClose={() => setShowWordModal(false)}
                        showNotification={showNotification}
                        navigate={navigate}
                    />
                </div>
            )}

            {showAnalizeModal && (
                <div className={styles.modalOverlay}>
                    <AnalizeModalWindow
                        speaking={false}
                        text={analizeModalData.text}
                        comment={analizeModalData.comment}
                        onClose={onCloseCallback}
                        showNotification={showNotification}
                        navigate={navigate}
                    />
                </div>
            )}

            <div className={`${styles.practiceCard}`}>
                <header className={`${styles.practiceHeader} ${styles.practiceHeaderGreen}`}>
                    <button className={`btn-flat white-text ${styles.headerBack}`} onClick={returnToRoleSelection} disabled={typing}
                            type="button" aria-label="Back to translation sets">
                                <i className="material-icons">arrow_back</i>
                            </button>
                    <div className={styles.headerTitle}>
                        <strong>{role?.summary}</strong>
                        <span>Translation practice · one phrase at a time</span>
                    </div>
                </header>

                <div className={styles.chatArea}>
                            <MainContainer style={{height: '100%'}}>
                                <ChatContainer>
                                    <MessageList
                                        scrollBehavior="smooth"
                                        typingIndicator={typing ? (
                                            <div style={{width: '100%'}}>
                                                <TypingIndicator
                                                    content={expectedWaitTime > 0 ? `AI is thinking... ${timeLeft.toFixed(1)}s` : "AI is thinking..."}/>
                                                {expectedWaitTime > 0 &&
                                                    <PreflightLoader expectedWaitTime={expectedWaitTime}
                                                                     compact={true} hideText={true}/>}
                                            </div>
                                        ) : null}
                                    >
                                        {userMessages !== null && userMessages.messages.map((message, idx) => (
                                            <Message
                                                key={idx}
                                                model={{
                                                    type: "custom",
                                                    direction: message.type.toLowerCase() === 'incoming' ? "incoming" : "outgoing",
                                                    position: "single"
                                                }}
                                            >
                                                <Message.CustomContent>
                                                    <div className={styles.messageText}>
                                                        {renderInteractiveText(message.text)}
                                                    </div>
                                                    {!message.result && (
                                                        <div className={styles.feedbackError}>
                                                            <i className="material-icons">error_outline</i>
                                                            Not quite — use the feedback, then try again.
                                                        </div>
                                                    )}
                                                    {message.comment !== null && (
                                                        <div className="right-align">
                                                            <button
                                                                className={`btn-flat btn-small blue-text ${styles.messageAction}`}
                                                                onClick={() => {
                                                                    setAnalizeModalData({
                                                                        text: message.text,
                                                                        comment: message.comment
                                                                    });
                                                                    setShowAnalizeModal(true);
                                                                }}
                                                            >
                                                                <i className="material-icons">info_outline</i>
                                                                View feedback
                                                            </button>
                                                        </div>
                                                    )}
                                                </Message.CustomContent>
                                            </Message>
                                        ))}

                                        {messages !== null && currentIndex < messages.messages.length && (
                                            <Message
                                                model={{direction: messages.messages[currentIndex].type.toLowerCase() === 'incoming' ? "incoming" : "outgoing"}}>
                                                <Message.CustomContent>
                                                    <div className={styles.translationPrompt}>
                                                        <div className={styles.translationPromptLabel}>Translate into English</div>
                                                        <div className={styles.translationPromptText}>
                                                        {messages.messages[currentIndex].textRu}
                                                    </div>
                                                    </div>
                                                </Message.CustomContent>
                                            </Message>
                                        )}
                                        {messages !== null && currentIndex >= messages.messages.length && (
                                            <div className={styles.completeState}>
                                                <i className="material-icons">check_circle</i>
                                                <h3>Set complete!</h3>
                                                <p>All translations in this set are complete.</p>
                                            </div>
                                        )}
                                    </MessageList>
                                    <MessageInput
                                        placeholder={typing ? "Checking your answer…" : "Type your translation in English…"}
                                        value={inputText}
                                        onChange={val => setInputText(val)}
                                        onSend={sendMessage}
                                        onPaste={handlePaste}
                                        attachButton={false}
                                        disabled={typing || (messages && currentIndex >= messages.messages.length)}
                                        sendOnReturnDisabled={typing || (messages && currentIndex >= messages.messages.length)}
                                    />
                                </ChatContainer>
                            </MainContainer>
                        </div>
            </div>
        </main>
    );
};

export default Translating;
